import { LiveFromParliamentSkeleton } from 'components/Dashboard/LiveFromParliament/EventListSkeleton';
import EventsList from 'components/Dashboard/LiveFromParliament/EventsList';
import EventsListEmpty from 'components/Dashboard/LiveFromParliament/EventsListEmpty';
import SelectedEvent from 'components/Dashboard/LiveFromParliament/SelectedEvent';
import Navigation from 'components/Dashboard/Navigation';
import { CreateAlertPopup } from 'components/Dashboard/Navigation/NotFeaturePopups';
import useGetRealTimeEvents from 'components/Dashboard/ParliamentLive/useGetRealtimeEvents';
import getUrlParam, { decodeUrlQueryValue } from 'components/Dashboard/utilities/getUrlParam';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';
import Footer from 'components/home/Footer';
import useScrollBody from 'components/home/useScrollBody';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

function LiveFromParliament() {
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [remainingSpaceBoxHeight, setRemainingSpaceBoxHeight] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const { getRealTimeEvents } = useGetRealTimeEvents();
  const isMobile = useIsMobile();

  useScrollBody();

  const sortResults = (a, b) => {
    return (
      new Date(a.actualStartDateTime ?? a.scheduledStartDateTime).getTime() -
      new Date(b.actualStartDateTime ?? b.scheduledStartDateTime).getTime()
    );
  };

  const callEventsFunc = useRef(null);
  callEventsFunc.current = async (source) => {
    try {
      setIsLoading(true);
      const results = await getRealTimeEvents({ source, needsAuthentication: false });
      let finalHits = [...results?.sort(sortResults)];
      setEvents(finalHits);
      const firstLiveEvent = finalHits.filter((item) => !!item?.transcriberRunning)?.[0];
      setTimeout(() => {
        setSelectedEvent(firstLiveEvent ?? finalHits?.[0]);
      }, 100);
      setIsLoading(false);
    } catch (error) {}
  };

  useEffect(() => {
    const source = new AbortController();
    callEventsFunc.current({ source });
    return () => {
      source.abort('Events cancelled by user');
    };
  }, []);

  useEffect(() => {
    const lockScrolling = (e) => {
      const overscrollBehaviorContainElem =
        e.target.closest('.overscroll-behavior-contain') && !e.target.closest('.ScrollbarsCustom-Content');
      if (!!overscrollBehaviorContainElem) {
        e.preventDefault();
      }
    };
    window.addEventListener('DOMMouseScroll', lockScrolling, false);
    document.addEventListener('wheel', lockScrolling, { passive: false });
    return () => {
      window.removeEventListener('DOMMouseScroll', lockScrolling, false);
      document.removeEventListener('wheel', lockScrolling, { passive: false });
    };
  }, []);

  const Title = () => {
    return (
      <>
        <h3 className='text-uppercase font-weight-bold paragraph-p2 main-light-text'>Political monitoring</h3>
        <h3 className='events-list-title font-weight-bold specific-bottom-margin-16'>Live from Parliament</h3>
      </>
    );
  };

  return (
    <>
      <Navigation />
      <section className='pb-5 pb-lg-5 bg-salmon overflow-hidden'>
        <div
          className={`d-lg-flex ${events?.length === 0 && !isLoading ? '' : 'justify-content-between'} pr-3 pr-lg-5 main-content-wrapper mx-auto gap-30 free-tools-container ${isMobile ? 'pt-4' : 'pt-5'}`}
        >
          {isLoading ? (
            <LiveFromParliamentSkeleton title={<Title />} />
          ) : (
            <>
              {events?.length === 0 ? (
                <div className='pl-3 pl-lg-5 max-width-none flex-grow-1'>
                  <EventsListEmpty />
                </div>
              ) : (
                <>
                  <div
                    className={`position-relative flex-grow-1 overscroll-behavior-contain ${events?.length > 0 ? '' : 'max-width-none'}`}
                  >
                    <div className={`pl-3 pl-lg-5 free-tools-title  ${events?.length > 0 ? '' : 'w-50'}`}>
                      <Title />
                      <ActionBar setSelectedEvent={setSelectedEvent} />
                    </div>
                    <EventsList
                      selectedEvent={selectedEvent}
                      setSelectedEvent={setSelectedEvent}
                      resultsToRender={events}
                      isLoading={isLoading}
                      sortResults={sortResults}
                      remainingSpaceBoxHeight={remainingSpaceBoxHeight}
                      setRemainingSpaceBoxHeight={setRemainingSpaceBoxHeight}
                    />
                  </div>
                  {events?.length > 0 && (
                    <div className='flex-grow-1 overscroll-behavior-contain d-none d-lg-block'>
                      <SelectedEvent selectedEvent={selectedEvent} />
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </section>
      <Footer />
    </>
  );
}

const ActionBar = ({ setSelectedEvent }) => {
  const [searchValue, setSearchValue] = useState('');
  const [isOpenCreateAlertPopup, setIsOpenCreateAlertPopup] = useState(false);
  const inputSearch = useRef(null);

  const location = useLocation();

  useEffect(() => {
    const searchQuery = getUrlParam('search') ? decodeUrlQueryValue(getUrlParam('search')) : '';
    setSearchValue(searchQuery);
  }, [location.search]);

  return (
    <>
      <div className='d-flex align-items-center'>
        <div className='rounded-pill border flex-centered specific-right-padding-7 specific-left-padding-15 specific-y-padding-6 flex-grow-1 bg-white specific-right-margin-10 search-events-input'>
          <div className='flex-grow-1 d-flex align-items-center'>
            <span className='icon-search mr-2'></span>
            <input
              className='input-empty w-100 paragraph-p1 pr-2'
              placeholder='Search events'
              value={searchValue}
              ref={inputSearch}
              onChange={(e) => {
                setSearchValue(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.keyCode === 13) {
                  inputSearch.current.blur();
                  setIsOpenCreateAlertPopup(true);
                } else if (e.metaKey && e.key === 'a') {
                  e.preventDefault();
                  inputSearch.current.select();
                } else if (e.keyCode === 9) {
                  e.preventDefault();
                }
              }}
            />
          </div>
          <div
            className='close-icon-container'
            style={{
              visibility: searchValue !== '' ? 'visible' : 'hidden',
            }}
          >
            <button
              className='general-button close-icon'
              data-tooltip-content='Clear search'
              data-tooltip-id='general-tooltip'
              data-tooltip-place='right'
              onClick={(e) => {
                e.stopPropagation();
                setSearchValue('');
              }}
            />
          </div>
        </div>
        <Link
          to={`/settings/alerts?returnUrl`}
          className='px-3 px-md-4 specific-y-padding-6 rounded-pill border bg-main-white general-button hovered-button link-hover reset-link nowrap-item'
          onClick={(e) => {
            e.preventDefault();
            setIsOpenCreateAlertPopup(true);
          }}
        >
          <span
            className=' d-inline-block icon-alert-medium paragraph-p2'
            style={{ marginTop: '-2px', verticalAlign: 'middle', marginRight: '8px' }}
          />
          Create an alert
        </Link>
      </div>
      <CreateAlertPopup isOpen={isOpenCreateAlertPopup} setIsOpen={setIsOpenCreateAlertPopup} />
    </>
  );
};

export default LiveFromParliament;
